<template>
  <div class="rds-page">

    <FiPageRail title="Admin Tools" />

    <div class="rds-card-grid">
      <router-link
        v-for="card in cards"
        :key="card.route"
        :to="{ name: card.route }"
        class="rds-card"
      >
        <div class="rds-card-icon"><i :class="['fa', card.icon]"></i></div>
        <div class="rds-card-title">{{ card.name }}</div>
        <div class="rds-card-desc">{{ card.description }}</div>
      </router-link>
    </div>

    <h3 class="rds-section-title">Preview</h3>
    <p class="rds-section-sub">Early builds of new operations surfaces. Staff only while in alpha.</p>

    <div class="rds-card-grid">
      <router-link
        v-for="card in previewCards"
        :key="card.route"
        :to="{ name: card.route, query: card.query }"
        class="rds-card"
      >
        <div class="rds-card-icon"><i :class="['fa', card.icon]"></i></div>
        <div class="rds-card-title">{{ card.name }}</div>
        <div class="rds-card-desc">{{ card.description }}</div>
      </router-link>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'admin_tools_dashboard',
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    cards() {
      return [
        {
          name: 'Summary Dashboard',
          route: 'admin_dashboard',
          icon: 'fa-tachometer',
          description: 'High-level overview of system health, tenant activity, and key metrics.',
        },
        {
          name: 'Account & Billing Stats',
          route: 'tenant_manager',
          icon: 'fa-building',
          description: 'Tenant account details, subscription status, and billing statistics.',
        },
        {
          name: 'API Usage',
          route: 'api_usage_dashboard',
          icon: 'fa-bar-chart',
          description: 'API request volumes, response times, and usage breakdowns by tenant.',
        },
        {
          name: 'SMS Usage',
          route: 'sms_usage_dashboard',
          icon: 'fa-comment-o',
          description: 'SMS send/receive volumes, delivery status, and actual carrier costs by tenant.',
        },
        {
          name: 'Settings History',
          route: 'admin_settings_history',
          icon: 'fa-history',
          description: 'Settings changes across every tenant, filterable by tenant, tenant type, and telematics type.',
        },
        {
          name: 'Process Manager',
          route: 'process_manager_list',
          icon: 'fa-cogs',
          description: 'View and manage background processing jobs across all tenants.',
        },
        {
          name: 'Actuals Simulator',
          route: 'actuals_simulator',
          icon: 'fa-play-circle',
          description: 'Simulate actuals processing to test and debug booking matching logic.',
        },
        {
          name: 'URL Shortener',
          route: 'url_shortener',
          icon: 'fa-link',
          description: 'Create and manage short URLs for sharing fleet integration links.',
        },
        {
          name: 'Cache Manager',
          route: 'cache_manager',
          icon: 'fa-database',
          description: 'Inspect and flush cached data for tenants and system resources.',
        },
      ]
    },
    // ops surfaces relocated from the navbar's ops dropdown while alpha
    // (FI-1815); the surfaces degrade to fact states when a data source
    // is missing, so no per-item gates
    previewCards() {
      return [
        {
          name: 'Today Board',
          route: 'ops_board',
          icon: 'fa-calendar-check-o',
          description: 'Exception-first dispatch board for today\'s departures, active trips, and returns.',
        },
        // ops events left this group when it became a main nav item for staff
        // as well as customers
        {
          name: 'Vehicle Status Manager',
          route: 'vsm_board',
          query: { tenant: this.user.tenant },
          icon: 'fa-bus',
          description: 'Redesigned vehicle status board. The classic version stays in the main navigation.',
        },
        {
          name: 'Departure Board',
          route: 'departure_board',
          query: { tenant: this.user.tenant },
          icon: 'fa-clock-o',
          description: 'Redesigned departure board with display modes suited to wall screens.',
        },
      ]
    },
  },
}
</script>

<style scoped>
/* ── Page wrapper ───────────────────────────────────────────────────── */
.rds-page {
  padding-bottom: 40px;
}

.rds-section-sub {
  color: #555;
  font-size: 14px;
  margin-bottom: 28px;
}

.rds-section-title {
  font-size: 19px;
  font-weight: bold;
  color: #222;
  margin-top: 36px;
  margin-bottom: 6px;
}

/* ── Card grid ──────────────────────────────────────────────────────── */
.rds-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
  padding-right: 16px;
}

.rds-card {
  border: 1px solid #d0d8e4;
  border-radius: 6px;
  padding: 16px;
  background: #fff;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 3px rgba(70,130,180,0.08);
  transition: box-shadow 0.15s, border-color 0.15s;
  cursor: pointer;
  text-decoration: none !important;
}

.rds-card:hover {
  border-color: var(--fi-rail-accent);
  box-shadow: 0 2px 8px rgba(70,130,180,0.18);
  text-decoration: none !important;
}

.rds-card-icon {
  font-size: 22px;
  color: var(--fi-rail-accent);
  margin-bottom: 10px;
}

.rds-card-title {
  font-size: 15px;
  font-weight: bold;
  color: #222;
  margin-bottom: 6px;
}

.rds-card-desc {
  font-size: 13px;
  color: #666;
  flex: 1;
  line-height: 1.4;
}
</style>
